/* eslint-disable no-nested-ternary */
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useFormContext, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import FormHelperText from '@mui/material/FormHelperText';

import Iconify from '../iconify';
import { fData } from '../../utils/format-number';

// ----------------------------------------------------------------------
// Style helpers (hover / drag animations)
// ----------------------------------------------------------------------

const dropzoneTransition = (theme) =>
  theme.transitions.create(['border-color', 'background-color', 'transform', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  });

const dropzoneHoverSx = (theme) => ({
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
});

const dropzoneActiveSx = (theme) => ({
  transform: 'scale(1.01)',
  borderColor: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
});

const dropzoneRejectSx = (theme) => ({
  transform: 'scale(1.01)',
  borderColor: theme.palette.error.main,
  backgroundColor: alpha(theme.palette.error.main, 0.08),
  boxShadow: `0 0 0 4px ${alpha(theme.palette.error.main, 0.12)}`,
});

const dropzoneErrorSx = (theme) => ({
  borderColor: theme.palette.error.main,
  backgroundColor: alpha(theme.palette.error.main, 0.04),
});

// ----------------------------------------------------------------------
// Preview helpers
// ----------------------------------------------------------------------

function SingleFilePreview({ file }) {
  const previewUrl = useMemo(() => {
    if (typeof file === 'string') return file;
    if (file instanceof File) return URL.createObjectURL(file);
    return '';
  }, [file]);

  if (!previewUrl) return null;

  return (
    <Box
      component="img"
      src={previewUrl}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
      sx={{
        top: 8,
        left: 8,
        borderRadius: 1,
        position: 'absolute',
        width: 'calc(100% - 16px)',
        height: 'calc(100% - 16px)',
        objectFit: 'cover',
      }}
    />
  );
}

SingleFilePreview.propTypes = {
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

function MultiFilePreview({ files, onRemove }) {
  if (!files?.length) return null;

  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      {files.map((file, index) => {
        const key = typeof file === 'string' ? file : file.name + index;
        const name = typeof file === 'string' ? file.split('/').pop() : file.name;
        const size = typeof file === 'string' ? '' : fData(file.size);

        return (
          <Stack
            key={key}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              p: 1,
              borderRadius: 1,
              border: (t) => `solid 1px ${t.palette.divider}`,
              transition: (t) => t.transitions.create(['background-color']),
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Iconify icon="eva:file-fill" width={28} sx={{ color: 'text.secondary' }} />
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {size}
              </Typography>
            </Stack>
            {onRemove && (
              <IconButton size="small" onClick={() => onRemove(file)}>
                <Iconify icon="mingcute:close-line" width={18} />
              </IconButton>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

MultiFilePreview.propTypes = {
  files: PropTypes.array,
  onRemove: PropTypes.func,
};

export function UploadAvatar({
  error,
  file,
  disabled,
  helperText,
  sx,
  maxSize,
  uploadText = 'Upload photo',
  ...other
}) {
  const theme = useTheme();

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    multiple: false,
    disabled,
    accept: {
      'image/*': [],
    },
    maxSize,
    ...other,
  });

  // ---------------------------------------------------------------------------
  // Preview URL
  // ---------------------------------------------------------------------------

  const previewUrl = useMemo(() => {
    if (typeof file === 'string') return file;

    if (file instanceof File) {
      return URL.createObjectURL(file);
    }

    if (file?.preview) return file.preview;

    if (file?.url) return file.url;

    return '';
  }, [file]);

  // ---------------------------------------------------------------------------
  // Preview
  // ---------------------------------------------------------------------------

  const renderPreview = previewUrl && (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 'inherit',
      }}
    >
      <Box
        component="img"
        src={previewUrl}
        alt="Upload preview"
        onError={(event) => {
          event.currentTarget.style.display = 'none';
        }}
        sx={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: 'inherit',

          transition: dropzoneTransition(theme),

          ...(isDragActive && {
            transform: 'scale(1.06)',
            opacity: 0.9,
          }),
        }}
      />
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Placeholder / Hover Overlay
  // ---------------------------------------------------------------------------

  const renderPlaceholder = (
    <Box
      className="upload-placeholder"
      sx={{
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',

        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',

        borderRadius: 'inherit',
        position: 'absolute',

        transition: dropzoneTransition(theme),

        backgroundColor: (t) => alpha(t.palette.grey[500], isDragActive ? 0.16 : 0.08),

        color: 'text.disabled',

        // IMPORTANT:
        // Hide placeholder when preview exists
        opacity: previewUrl ? 0 : 1,

        ...(previewUrl && {
          // Original hover overlay
          backgroundColor: alpha('#000', 0.55),
          color: '#fff',

          // Keep original hover behavior
          '.upload-root:hover &': {
            opacity: 1,
          },
        }),

        ...(isDragActive && {
          opacity: 0.72,
          transform: 'scale(1.04)',
        }),
      }}
    >
      <Iconify
        icon="solar:camera-add-bold"
        width={24}
        sx={{
          transition: dropzoneTransition(theme),

          transform: isDragActive ? 'scale(1.15) rotate(-8deg)' : 'scale(1)',
        }}
      />

      <Box
        sx={{
          mt: 0.5,
          fontSize: 12,
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        {isDragReject ? 'File not allowed' : isDragActive ? 'Drop it here' : uploadText}
      </Box>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Main Component
  // ---------------------------------------------------------------------------

  return (
    <Box>
      <Box
        {...getRootProps()}
        className="upload-root"
        sx={{
          position: 'relative',

          // ORIGINAL DESIGN
          p: 1,
          m: 'auto',
          width: 144,
          height: 144,

          cursor: 'pointer',
          overflow: 'hidden',

          borderRadius: 2,

          border: (t) => `1px dashed ${t.palette.divider}`,

          transition: dropzoneTransition(theme),

          // ORIGINAL HOVER
          ...dropzoneHoverSx(theme),

          // ORIGINAL DRAG ACTIVE
          ...(isDragActive && dropzoneActiveSx(theme)),

          // ORIGINAL DRAG REJECT
          ...(isDragReject && dropzoneRejectSx(theme)),

          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),

          ...(error && dropzoneErrorSx(theme)),

          ...sx,
        }}
      >
        <input {...getInputProps()} />

        <Box
          className="upload-wrapper"
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            borderRadius: 'inherit',
          }}
        >
          {renderPreview}
          {renderPlaceholder}
        </Box>
      </Box>

      {helperText}
    </Box>
  );
}

UploadAvatar.propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helperText: PropTypes.node,
  maxSize: PropTypes.number,
  sx: PropTypes.object,
  uploadText: PropTypes.string,
};

// ----------------------------------------------------------------------
// UploadBox (base, styled)
// ----------------------------------------------------------------------

export function UploadBox({
  placeholder,
  error,
  disabled,
  file,
  sx,
  helperText,
  acceptFile,
  ...other
}) {
  const theme = useTheme();

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    disabled,
    accept: acceptFile || other.accept,
    ...other,
  });

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          width: 64,
          height: 64,
          flexShrink: 0,
          display: 'flex',
          borderRadius: 1,
          cursor: 'pointer',
          alignItems: 'center',
          justifyContent: 'center',
          border: (t) => `1px dashed ${t.palette.divider}`,
          transition: dropzoneTransition(theme),
          ...dropzoneHoverSx(theme),
          ...(isDragActive && dropzoneActiveSx(theme)),
          ...(isDragReject && dropzoneRejectSx(theme)),
          ...(disabled && { opacity: 0.48, pointerEvents: 'none' }),
          ...(error && dropzoneErrorSx(theme)),
          ...sx,
        }}
      >
        <input {...getInputProps()} />

        {placeholder || (
          <Iconify
            icon={isDragReject ? 'solar:close-circle-bold' : 'eva:cloud-upload-fill'}
            width={28}
            sx={{
              color: isDragReject ? 'error.main' : 'text.disabled',
              transition: dropzoneTransition(theme),
              transform: isDragActive ? 'translateY(-3px) scale(1.1)' : 'translateY(0) scale(1)',
            }}
          />
        )}
      </Box>

      {helperText}
    </Box>
  );
}

UploadBox.propTypes = {
  acceptFile: PropTypes.object,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helperText: PropTypes.node,
  placeholder: PropTypes.node,
  sx: PropTypes.object,
};

// ----------------------------------------------------------------------
// Upload (base, styled)
// ----------------------------------------------------------------------

export function Upload({
  multiple,
  error,
  disabled,
  file,
  files,
  onDelete,
  onRemove,
  onUpload,
  helperText,
  sx,
  ...other
}) {
  const theme = useTheme();

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    multiple,
    disabled,
    ...other,
  });

  const hasFile = !multiple && !!file;
  const hasFiles = multiple && !!files?.length;

  const renderPlaceholder = (
    <Stack
      spacing={1.5}
      alignItems="center"
      justifyContent="center"
      sx={{ width: 1, textAlign: 'center' }}
    >
      <Iconify
        icon="eva:cloud-upload-fill"
        width={40}
        sx={{
          color: isDragReject ? 'error.main' : 'text.disabled',
          transition: dropzoneTransition(theme),
          transform: isDragActive ? 'translateY(-6px) scale(1.12)' : 'translateY(0) scale(1)',
        }}
      />

      <Stack spacing={0.5}>
        <Typography variant="h6" sx={{ transition: dropzoneTransition(theme) }}>
          {isDragReject
            ? 'File type not accepted'
            : isDragActive
            ? 'Drop the file here'
            : 'Drop or select file'}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Drop file here or click to&nbsp;
          <Typography
            component="span"
            variant="body2"
            sx={{ color: 'primary.main', textDecoration: 'underline' }}
          >
            browse
          </Typography>
          &nbsp;through your machine
        </Typography>
      </Stack>
    </Stack>
  );

  const renderSinglePreview = hasFile && !isDragActive && <SingleFilePreview file={file} />;

  return (
    <Box sx={{ width: 1, position: 'relative' }}>
      <Box
        {...getRootProps()}
        sx={{
          p: 5,
          outline: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
          bgcolor: 'transparent',
          border: (t) => `1px dashed ${t.palette.divider}`,
          transition: dropzoneTransition(theme),
          ...dropzoneHoverSx(theme),
          ...(isDragActive && dropzoneActiveSx(theme)),
          ...(isDragReject && dropzoneRejectSx(theme)),
          ...(disabled && { opacity: 0.48, pointerEvents: 'none' }),
          ...(hasFile && { padding: '28% 0' }),
          ...(error && dropzoneErrorSx(theme)),
          ...sx,
        }}
      >
        <input {...getInputProps()} />
        {renderSinglePreview || renderPlaceholder}
      </Box>

      {helperText}

      {hasFiles && <MultiFilePreview files={files} onRemove={onRemove} />}
    </Box>
  );
}

Upload.propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  files: PropTypes.array,
  helperText: PropTypes.node,
  multiple: PropTypes.bool,
  onDelete: PropTypes.func,
  onRemove: PropTypes.func,
  onUpload: PropTypes.func,
  sx: PropTypes.object,
};

// ----------------------------------------------------------------------
// RHFUploadAvatar
// ----------------------------------------------------------------------

export function RHFUploadAvatar({
  name,
  acceptFile,
  maxSize = 3 * 1024 * 1024,
  uploadText = 'Upload photo',
  ...other
}) {
  const { control } = useFormContext();

  const globalMaxSize = useSelector((state) => state.tenantPreference?.maxSizeOfDocuments);
  const effectiveMaxSize = (globalMaxSize || 3) * 1024 * 1024;

  const getFileErrorMessage = (file) => {
    if (file?.size > effectiveMaxSize) {
      return `File exceeds the maximum size of ${(effectiveMaxSize / (1024 * 1024)).toFixed(
        2
      )} MB.`;
    }
    return null;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const fileErrorMessage = field.value ? getFileErrorMessage(field.value) : null;

        return (
          <div>
            <UploadAvatar
              error={!!error || !!fileErrorMessage}
              file={field.value}
              maxSize={effectiveMaxSize}
              uploadText={uploadText}
              helperText={
                <>
                  {!!error && (
                    <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                      {error.message}
                    </FormHelperText>
                  )}
                  {!!fileErrorMessage && (
                    <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                      {fileErrorMessage}
                    </FormHelperText>
                  )}
                </>
              }
              {...field}
              {...other}
            />
          </div>
        );
      }}
    />
  );
}

RHFUploadAvatar.propTypes = {
  name: PropTypes.string.isRequired,
  acceptFile: PropTypes.object.isRequired,
  maxSize: PropTypes.number,
  uploadText: PropTypes.string,
};

// ----------------------------------------------------------------------
// RHFUploadBox
// ----------------------------------------------------------------------

export function RHFUploadBox({
  name,
  helperText,
  acceptFile,
  maxSize = 3 * 1024 * 1024,
  ...other
}) {
  const { control } = useFormContext();
  const globalMaxSize = useSelector((state) => state.tenantPreference?.maxSizeOfDocuments);
  const maxSizeOfDocs = (globalMaxSize || 3) * 1024 * 1024;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <UploadBox
          accept={acceptFile}
          acceptFile={acceptFile}
          file={field.value}
          error={!!error}
          helperText={
            (!!error || helperText) && (
              <FormHelperText error={!!error} sx={{ px: 2 }}>
                {error ? error?.message : helperText}
              </FormHelperText>
            )
          }
          {...other}
          maxSize={maxSizeOfDocs}
        />
      )}
    />
  );
}

RHFUploadBox.propTypes = {
  helperText: PropTypes.string,
  name: PropTypes.string.isRequired,
  acceptFile: PropTypes.object.isRequired,
  maxSize: PropTypes.number,
};

// ----------------------------------------------------------------------
// RHFUpload
// ----------------------------------------------------------------------

export function RHFUpload({ name, multiple, acceptFile, helperText, ...other }) {
  const { control } = useFormContext();

  const globalMaxSize = useSelector((state) => state.tenantPreference?.maxSizeOfDocuments);
  const maxSizeOfDocs = (globalMaxSize || 3) * 1024 * 1024;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) =>
        multiple ? (
          <Upload
            multiple
            accept={acceptFile}
            files={field.value}
            error={!!error}
            helperText={
              (!!error || helperText) && (
                <FormHelperText error={!!error} sx={{ px: 2 }}>
                  {error ? error?.message : helperText}
                </FormHelperText>
              )
            }
            {...other}
            maxSize={maxSizeOfDocs}
          />
        ) : (
          <Upload
            file={field.value}
            accept={acceptFile}
            error={!!error}
            helperText={
              (!!error || helperText) && (
                <FormHelperText error={!!error} sx={{ px: 2 }}>
                  {error ? error?.message : helperText}
                </FormHelperText>
              )
            }
            {...other}
            maxSize={maxSizeOfDocs}
          />
        )
      }
    />
  );
}

RHFUpload.propTypes = {
  name: PropTypes.string.isRequired,
  multiple: PropTypes.bool,
  acceptFile: PropTypes.object.isRequired,
  helperText: PropTypes.string,
};
